import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, Select, DatePicker, Row, Col, Switch, Steps, Button, Radio } from 'antd';
import CBInputNumber from './CBInputNumber';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../i18n/libelles';
import axiosInstance from '../variables/api/axiosInstance';
import {
  CATEGORIES_PERMIS, STATUTS_ACTIVITE, splitCategoriesPermis,
  NATURES_PARTIE_ADVERSE, NATURE_PARTIE_ADVERSE_MAP, natureDepuisAdversaire,
} from '../variables/constants';
import SelectWithAdd from './SelectWithAdd';
import NegociationRcPanel from './NegociationRcPanel';
import CBModal from './CBModal';
import { fieldLabel, RequiredLegend } from './FieldLabel';
import {
  situationsPour, estUneVictime, champsServeur, situationDepuisFiche,
} from '../utils/situationAdverse';
import { libellePaysOrganisme } from '../variables/paysCedeao';
import ChampResponsabilite, { responsabiliteDepuisTaux, tauxRcDepuisSaisie } from './ChampResponsabilite';
const { TextArea } = Input;

const GREEN = '#1a6b3c';
const GREEN_DARK = '#134d2b';
const GREEN_SOFT = '#ebf4ee';
const GREEN_LINE = '#c7e0cf';
const LINE = '#e7e9ee';
const PAPER = '#fafaf9';
const INK = '#0b0f14';
const INK_MUTED = '#5a6676';

const lbl = (text, required) => fieldLabel(text, required);
const sectionLabel = {
  fontSize: 11, fontWeight: 600, letterSpacing: '1.4px',
  textTransform: 'uppercase', color: INK_MUTED, marginBottom: 14,
};

const inputH = { style: { height: 38, borderRadius: 8, borderColor: LINE, fontSize: 14 } };
const dateH = { style: { ...inputH.style, width: '100%' } };

// Sentinelle « nationalité hors CEDEAO » : le référentiel Pays ne contient que les
// 14 États membres CEDEAO, un adversaire d'un autre pays n'y figure donc pas.
const HORS_CEDEAO = '__HORS_CEDEAO__';

/* LA SITUATION DE LA PARTIE ADVERSE EST UN SEUL CHOIX A QUATRE VALEURS.

   Deux questions disaient une seule chose : « Type de dommage » - corporel, matériel,
   mixte - puis, si le dommage touchait une personne, « Situation » - blessé, décédé,
   l'État, morale privée. Il fallait comprendre l'articulation des deux pour saisir qu'un
   conducteur blessé devient une victime du dossier.

   Blessé, Décédé, Matériel, Adversaire : le choix se lit comme une phrase et détermine
   les deux champs du serveur. La traduction vit dans `utils/situationAdverse`, avec ses
   tests - et dans les deux sens, car une fiche enregistrée avant ce changement doit se
   relire sur un des quatre choix. Les libellés des quatre valeurs sont traduits là-bas,
   sous `commun:adversaireModal.situations`. */
const CHAMPS_VICTIME = ['sexe', 'statutActivite', 'profession', 'revenuMensuel', 'paysResidenceTrackingId'];

const GENRES_VEHICULE = avecLibelleTraduit([
  { value: 'VOITURE' },
  { value: 'CAMIONETTE' },
  { value: 'CAMION' },
  { value: 'BUS' },
  { value: 'TRACTEUR' },
  { value: 'REMORQUE' },
  { value: 'MOTO' },
  { value: 'TRICYCLE' },
  { value: 'AUTRE' },
], 'commun:adversaireModal.genresVehicule');

/* Options du sélecteur de nature : libellé riche dans la liste déroulante,
   libellé compact une fois le choix fait. Construites au rendu, pas au chargement :
   les libellés des natures suivent la langue de l'interface. */
const optionsNature = () => NATURES_PARTIE_ADVERSE.map((n) => ({
  value: n.key,
  selectedLabel: (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 16 }}>{n.icone}</span>
      <span style={{ fontWeight: 500 }}>{n.label}</span>
    </span>
  ),
  label: (
    <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '2px 0' }}>
      <span style={{ fontSize: 17, lineHeight: 1 }}>{n.icone}</span>
      <span style={{ minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 500, color: INK, lineHeight: 1.4 }}>
          {n.label}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: INK_MUTED, lineHeight: 1.4 }}>
          {n.desc}
        </span>
      </span>
    </span>
  ),
}));

/**
 * Modal d'ajout / édition d'une partie adverse.
 *
 * Le parcours démarre par la NATURE de la partie adverse, et c'est elle qui
 * décide de tout le reste - on ne demande jamais un champ qui n'a pas de sens :
 *
 *   🚗 Véhicule            → Nature · Conducteur · Véhicule · Dommages
 *   🚶 Piéton              → Nature · Piéton · Dommages
 *                            (ni assureur adverse, ni type de dommage : un
 *                             piéton n'est pas assuré et son dommage est corporel)
 *   🏠 Bâtiment · 📦 Objet · ⚠️ Autre
 *                          → Nature · Bien & dégâts
 *                            (un mur n'a ni identité, ni bilan humain, ni RC)
 *
 * Le genre précis de l'engin (moto, tricycle, camion…) se choisit à l'étape
 * Véhicule : il ne dédouble pas la liste des natures.
 *
 * PRÉJUDICE CORPOREL. Un piéton, ou un engin dont le dommage est corporel ou
 * mixte, demande la situation de la personne. Blessée ou décédée, la partie
 * adverse est aussi une VICTIME du dossier (voir `estVictimeIndemnisable`) : on
 * recueille alors ce que son indemnisation exige.
 */
export default function AdversaireModal({ open, onClose, onSave, onRcUpdated, initial, loading = false }) {
  const { t } = useTranslation('commun');
  const [form] = Form.useForm();
  const [compagnies, setCompagnies] = useState([]);
  const [paysList, setPaysList] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [natureKey, setNatureKey] = useState('VEHICULE');

  const nom = Form.useWatch('nom', form);
  const prenoms = Form.useWatch('prenoms', form);
  const numeroPermis = Form.useWatch('numeroPermis', form);
  const aRemorque = Form.useWatch('aRemorque', form);
  const genreVehicule = Form.useWatch('genreVehicule', form);
  const nationaliteChoix = Form.useWatch('nationaliteChoix', form);
  const situation = Form.useWatch('situationAdverse', form);

  const nature = NATURE_PARTIE_ADVERSE_MAP[natureKey] || NATURE_PARTIE_ADVERSE_MAP.VEHICULE;
  const typeAdversaire = nature.typeAdversaire;

  const estEngin = typeAdversaire === 'ENGINS';
  const estPieton = typeAdversaire === 'PIETON';
  /* Bâtiment, objet, autre : un bien matériel, sans identité ni bilan humain. */
  const estBien = !estEngin && !estPieton;

  /* Une partie adverse qui subit un préjudice CORPOREL est aussi une victime : elle
     figure parmi les victimes du dossier et s'indemnise comme elles (offre, pièces,
     maturité). Un piéton heurté l'est par nature ; un engin, dès que son dommage est
     corporel ou mixte. On lui demande alors sa situation, et - blessé ou décédé - ce
     que le calcul de l'indemnité exige. */
  /* UNE SEULE CONDITION DESORMAIS : la situation choisie. Elle valait auparavant
     `estPieton || dommage corporel ou mixte`, puis le second champ la precisait - deux
     conditions a tenir en accord pour une seule idee. */
  const estVictime = estUneVictime(situation);
  const montrerSituation = situationsPour({ estPieton, estEngin }).length > 0;
  const champsPrejudice = montrerSituation
    ? ['situationAdverse', ...(estVictime ? CHAMPS_VICTIME : [])]
    : [];

  /* Le bloc remorque s'ouvre soit sur le switch, soit dès que l'engin adverse
     est lui-même une remorque / semi-remorque. */
  const montrerRemorque = estEngin && (!!aRemorque || genreVehicule === 'REMORQUE');

  const degatsLabel = estVictime
    ? t('adversaireModal.degats.corporels')
    : t('adversaireModal.degats.materiels');

  useEffect(() => {
    if (!open || compagnies.length > 0) return;
    axiosInstance.get('/v1/organismes/type/COMPAGNIE_MEMBRE')
      .then(r => setCompagnies(r.data?.data || []))
      .catch(() => setCompagnies([]));
  }, [open, compagnies.length]);

  /* Deux compagnies de pays différents portent parfois le même nom (« GA », « AXA ») :
     le pays s'affiche dans la liste et entre dans la recherche. La valeur reste la
     raison sociale seule - c'est ce que `assureur_adverse` stocke -, d'où un champ
     fermé qui n'affiche que le nom plutôt que le pays d'une homonyme. */
  const optionsCompagnies = useMemo(() => compagnies
    .map(c => {
      const pays = libellePaysOrganisme(c);
      return {
        value: c.raisonSociale,
        nom: c.raisonSociale,
        recherche: `${c.raisonSociale} ${pays} ${c.codePays || ''} ${c.codePaysBCB || ''}`,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.raisonSociale}</span>
            {pays && <span style={{ flexShrink: 0, fontSize: 12, color: INK_MUTED }}>{pays}</span>}
          </div>
        ),
      };
    })
    .sort((a, b) => (a.nom || '').localeCompare(b.nom || '')), [compagnies]);
  const filtreCompagnie = (saisie, option) =>
    (option?.recherche || '').toLowerCase().includes(saisie.toLowerCase());

  // Référentiel pays CEDEAO + restauration de la nationalité une fois la liste connue :
  // si la valeur enregistrée correspond à un pays CEDEAO on le pré-sélectionne, sinon on
  // bascule sur « Hors CEDEAO » et on remet la valeur dans le champ « préciser ».
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    axiosInstance.get('/v1/pays')
      .then(r => {
        if (cancelled) return;
        const list = r.data?.data || [];
        setPaysList(list);
        const nat = (initial?.nationalite || '').trim();
        if (!nat) return;
        const estCedeao = list.some(p => p.libelle === nat);
        form.setFieldsValue(estCedeao
          ? { nationaliteChoix: nat, nationaliteAutre: undefined }
          : { nationaliteChoix: HORS_CEDEAO, nationaliteAutre: nat });
      })
      .catch(() => { if (!cancelled) setPaysList([]); });
    return () => { cancelled = true; };
  }, [open, initial, form]);

  useEffect(() => {
    if (!open) return;
    setCurrentStep(0);
    form.resetFields();
    if (initial) {
      setNatureKey(natureDepuisAdversaire(initial).key);
      form.setFieldsValue({
        ...initial,
        // Le backend renvoie une chaîne "A1,B" ; le multi-select attend un tableau.
        categoriesPermis: splitCategoriesPermis(initial.categoriesPermis),
        dateNaissance: initial.dateNaissance ? dayjs(initial.dateNaissance) : null,
        dateDelivrance: initial.dateDelivrance ? dayjs(initial.dateDelivrance) : null,
        prochaineVT: initial.prochaineVT ? dayjs(initial.prochaineVT) : null,
        estResponsable: responsabiliteDepuisTaux(initial.tauxRc),
        /* LA SITUATION SE RELIT DES DEUX COLONNES. Une fiche enregistree avant ce
           changement porte ETAT, MORALE_PRIVEE, ou un dommage MIXTE : sans cette
           traduction, le champ s'ouvrirait vide et l'enregistrer effacerait la
           situation de la partie. */
        situationAdverse: situationDepuisFiche(initial),
      });
    } else {
      setNatureKey('VEHICULE');
      form.setFieldsValue({
        situationAdverse: 'MATERIEL',
        blesses: 0,
        deces: 0,
      });
    }
  }, [open, initial, form]);

  /* Changement de nature : la précision « Autre » ne survit pas à un autre choix. */
  const choisirNature = (key) => {
    if (!NATURE_PARTIE_ADVERSE_MAP[key]) return;
    setNatureKey(key);
    if (key !== 'AUTRE') form.setFieldsValue({ typeAdversaireAutre: undefined });
    /* La situation choisie doit rester proposee par la nouvelle nature : « Materiel » ne
       vaut pas pour un pieton. Sinon le formulaire garderait une valeur que la liste
       n'offre plus - invisible a l'ecran, mais enregistree. */
    const nature = NATURE_PARTIE_ADVERSE_MAP[key].typeAdversaire;
    const permises = situationsPour({
      estPieton: nature === 'PIETON',
      estEngin: nature === 'ENGINS',
    }).map(s => s.value);
    if (!permises.includes(form.getFieldValue('situationAdverse'))) {
      form.setFieldsValue({ situationAdverse: undefined });
    }
  };

  const submit = async () => {
    try {
      const vals = await form.validateFields();
      // Nationalité : pays CEDEAO sélectionné → son libellé ; « Hors CEDEAO » → texte précisé.
      const nationalite = vals.nationaliteChoix === HORS_CEDEAO
        ? (vals.nationaliteAutre?.trim() || null)
        : (vals.nationaliteChoix || null);
      const avecRemorque = estEngin && (!!vals.aRemorque || vals.genreVehicule === 'REMORQUE');

      onSave({
        ...vals,
        typeAdversaire,
        nationalite: estBien ? null : nationalite,
        nationaliteChoix: undefined,
        nationaliteAutre: undefined,
        estAdversaire: true,
        dateNaissance: estBien ? null : (vals.dateNaissance?.format('YYYY-MM-DD') || null),
        dateDelivrance: estEngin ? (vals.dateDelivrance?.format('YYYY-MM-DD') || null) : null,
        prochaineVT: estEngin ? (vals.prochaineVT?.format('YYYY-MM-DD') || null) : null,
        // La précision n'a de sens que pour AUTRE.
        typeAdversaireAutre: typeAdversaire === 'AUTRE' ? vals.typeAdversaireAutre : null,

        /* LES DEUX COLONNES DU SERVEUR, DEDUITES DE LA SITUATION. `typeDommage` est nul
           pour « Adversaire » : la colonne l'accepte, et c'est la representation juste
           d'une partie identifiee dont aucun prejudice n'est retenu. */
        ...champsServeur(vals.situationAdverse, { estPieton, estEngin }),
        situationAdverse: undefined,
        // Un piéton et un mur n'ont pas de compagnie d'assurance.
        assureurAdverse: estEngin ? (vals.assureurAdverse || null) : null,

        /* Blessé ou décédé : la partie adverse devient aussi une victime. Sinon aucune
           donnée d'indemnisation résiduelle n'est conservée - l'utilisateur a pu saisir
           puis changer de situation. */
        ...(estUneVictime(vals.situationAdverse) ? {} : {
          sexe: null, statutActivite: null, profession: null, revenuMensuel: null, paysResidenceTrackingId: null,
        }),

        // Hors engin, aucun champ « engin » ne doit être persisté (données résiduelles
        // si l'utilisateur a saisi puis changé de nature).
        ...(estEngin ? { aRemorque: avecRemorque } : {
          numeroPermis: null, categoriesPermis: null, lieuDelivrance: null,
          immatriculation: null, marqueVehicule: null, modeleVehicule: null,
          genreVehicule: null, couleurVehicule: null, numeroChassis: null,
          capaciteVehicule: null, nbPersonnesABord: null, proprietaireVehicule: null,
          aRemorque: false,
        }),
        // Les détails remorque ne survivent pas au décochage.
        ...(avecRemorque ? {} : {
          remorqueMarque: null, remorqueModele: null,
          remorqueImmatriculation: null, remorqueAssureur: null,
        }),
        // Un bien n'a pas de prénom.
        ...(estBien ? { prenoms: null } : {}),
        /* Le bilan humain n'est saisi que pour un engin adverse (plusieurs
           occupants possibles) : ailleurs il reste à zéro. */
        ...(estEngin ? {} : { blesses: 0, deces: 0 }),
        /* Part de responsabilité DE LA PARTIE ADVERSE elle-même (conducteur ou piéton).
           Un bien heurté n'a pas de responsabilité : on reprend la valeur connue. */
        tauxRc: estBien || initial?.positionRc === 'TRANCHEE' ? (initial?.tauxRc ?? null) : tauxRcDepuisSaisie(vals),
        estResponsable: undefined,
      });
    } catch {
      /* validation inline */
    }
  };

  /* ────────────────────────────────────────────────────────────
     Découpage en étapes, piloté par la nature choisie.
     « Négociation RC » n'apparaît qu'en édition d'un adversaire
     déjà rattaché à un dossier.
     ──────────────────────────────────────────────────────────── */
  const steps = useMemo(() => [
    { key: 'nature', title: t('adversaireModal.etapes.nature') },
    ...(estEngin
      ? [
        { key: 'partie', title: t('adversaireModal.etapes.conducteur') },
        { key: 'vehicule', title: t('adversaireModal.etapes.vehicule') },
        { key: 'dommages', title: t('adversaireModal.etapes.dommages') },
      ]
      : estPieton
        // Un piéton tient en un seul écran : qui il est, et ce qu'il a subi.
        ? [{ key: 'partie', title: t('adversaireModal.etapes.pieton') }]
        : [{ key: 'bien', title: t('adversaireModal.etapes.bien') }]),
    ...(initial?.victimeTrackingId ? [{ key: 'rc', title: t('adversaireModal.etapes.negociationRc') }] : []),
  ], [estEngin, estPieton, initial, t]);

  // Recale l'étape courante si la liste se raccourcit (ex : on quitte « véhicule »
  // en passant sur Piéton alors qu'on était positionné dessus).
  useEffect(() => {
    setCurrentStep((s) => Math.min(s, steps.length - 1));
  }, [steps.length]);

  const isLastStep = currentStep === steps.length - 1;
  const stepKey = steps[currentStep]?.key;

  /* Une personne adverse doit rester identifiable : nom, prénom ou n° de permis.
     La règle est portée par le champ Nom, donc affichée inline. */
  const identifiable = estBien || Boolean(
    (nom && String(nom).trim()) ||
    (prenoms && String(prenoms).trim()) ||
    (estEngin && numeroPermis && String(numeroPermis).trim())
  );
  const regleIdentite = {
    validator: () => identifiable
      ? Promise.resolve()
      : Promise.reject(new Error(estEngin
        ? t('adversaireModal.identite.engin')
        : t('adversaireModal.identite.personne'))),
  };

  const fieldsByStep = {
    nature: typeAdversaire === 'AUTRE' ? ['typeAdversaireAutre'] : [],
    partie: [
      'nom', 'prenoms', 'dateNaissance', 'telephone', 'nationaliteChoix',
      ...(nationaliteChoix === HORS_CEDEAO ? ['nationaliteAutre'] : []),
      ...(estEngin
        ? ['numeroPermis', 'categoriesPermis', 'dateDelivrance', 'lieuDelivrance']
        : ['descriptionDegats', 'estResponsable', 'tauxRc', ...champsPrejudice]),
    ],
    vehicule: [
      'immatriculation', 'marqueVehicule', 'modeleVehicule', 'genreVehicule', 'couleurVehicule',
      'prochaineVT', 'capaciteVehicule', 'nbPersonnesABord', 'proprietaireVehicule',
      'aRemorque', 'numeroChassis',
      ...(montrerRemorque
        ? ['remorqueImmatriculation', 'remorqueMarque', 'remorqueModele', 'remorqueAssureur']
        : []),
    ],
    dommages: [
      'assureurAdverse', 'descriptionDegats',
      'blesses', 'deces', 'estResponsable', 'tauxRc',
      ...champsPrejudice,
    ],
    bien: ['nom', 'telephone', 'descriptionDegats'],
    rc: [],
  };

  /* Situation de la partie adverse : rendue à l'étape Piéton pour un piéton, à l'étape
     Dommages pour un engin - jamais les deux, les natures étant exclusives, donc jamais
     deux Form.Item de même nom montés ensemble. */
  const blocPrejudiceCorporel = (
    <div style={{ marginTop: 8, marginBottom: 8, padding: '14px 16px 2px', background: PAPER, border: `1px solid ${LINE}`, borderRadius: 10 }}>
      <div style={sectionLabel}>{estPieton ? t('adversaireModal.prejudice.pieton') : t('adversaireModal.prejudice.conducteur')}</div>
      <Form.Item
        name="situationAdverse"
        label={lbl(t('adversaireModal.champs.situation'), true)}
        rules={[{ required: true, message: t('adversaireModal.prejudice.precisezSituation') }]}
        extra={estVictime
          ? <span style={{ fontSize: 11.5, color: INK_MUTED }}>{t('adversaireModal.prejudice.figureraVictimes')}</span>
          : null}
      >
        {/* Les quatre situations, réduites à deux pour un piéton : il est une personne,
            « Matériel » lui serait un non-sens. */}
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          options={situationsPour({ estPieton, estEngin })}
        />
      </Form.Item>
      {estVictime && (
        <>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item name="sexe" label={lbl(t('adversaireModal.champs.sexe'))}>
                <Select {...inputH} allowClear placeholder={t('adversaireModal.selectionner')}
                  options={[{ value: 'M', label: t('adversaireModal.sexes.M') }, { value: 'F', label: t('adversaireModal.sexes.F') }]} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={16}>
              <Form.Item name="statutActivite" label={lbl(t('adversaireModal.champs.statutActivite'))}>
                <Select {...inputH} allowClear placeholder={t('adversaireModal.selectionner')}
                  options={STATUTS_ACTIVITE.map(s => ({ value: s.value, label: s.label }))} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="profession" label={lbl(t('adversaireModal.champs.profession'))}>
                <SelectWithAdd categorie="PROFESSION" placeholder={t('adversaireModal.selectionnerProfession')}
                  addLabel={t('adversaireModal.ajouterProfession')} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="revenuMensuel" label={lbl(t('adversaireModal.champs.revenuMensuel'))}>
                <CBInputNumber style={{ ...inputH.style, width: '100%' }} min={0} placeholder="0" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="paysResidenceTrackingId" label={lbl(t('adversaireModal.champs.paysResidence'))}>
                <Select {...inputH} allowClear showSearch optionFilterProp="label" placeholder={t('adversaireModal.champs.paysResidence')}
                  options={paysList.map(p => ({ label: p.libelle, value: p.paysTrackingId }))} />
              </Form.Item>
            </Col>
          </Row>
        </>
      )}
    </div>
  );

  /* Responsabilité de la partie adverse. Tranchée avec la compagnie, elle ne se
     saisit plus : la décision fait foi (le backend refuse aussi de la réécrire). */
  const rcTranchee = initial?.positionRc === 'TRANCHEE';
  const tauxTranche = initial?.pourcentageRcFinal ?? (initial?.tauxRc != null ? Number(initial.tauxRc) : null);
  /* La question ne se pose pas à l'ajout : la responsabilité s'établit avec la
     position RC (fenêtre « Position RC » du dossier), ou se corrige en cliquant sur
     Modifier. À l'enregistrement d'une nouvelle partie, `tauxRc` reste null. */
  const blocResponsabilite = !initial ? null : (
    <>
      <div style={sectionLabel}>{t('adversaireModal.responsabilite.titre')}</div>
      {rcTranchee ? (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: GREEN_SOFT, border: `1px solid ${GREEN_LINE}`, borderRadius: 8, fontSize: 13.5 }}>
          {Number(tauxTranche) > 0
            ? <Trans t={t} i18nKey="adversaireModal.responsabilite.responsableA" values={{ taux: tauxTranche }} components={{ 1: <strong /> }} />
            : <strong>{t('adversaireModal.responsabilite.nonResponsable')}</strong>}
          <span style={{ color: INK_MUTED }}> {t('adversaireModal.responsabilite.tranchee')}</span>
        </div>
      ) : (
        <ChampResponsabilite form={form} sujet={t('adversaireModal.sujetResponsabilite')} />
      )}
    </>
  );

  const goNext = async () => {
    try {
      await form.validateFields(fieldsByStep[stepKey] || []);
    } catch {
      return; // erreurs affichées inline par le Form, on reste sur l'étape
    }
    setCurrentStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const goPrev = () => setCurrentStep((s) => Math.max(s - 1, 0));

  return (
    <CBModal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 9, color: '#fff' }}>
          <span style={{ fontSize: 17 }}>{nature.icone}</span>
          <span style={{ fontSize: 16, fontWeight: 600 }}>
            {initial ? t('adversaireModal.titres.modifier') : t('adversaireModal.titres.ajouter')}
          </span>
        </span>
      }
      open={open}
      onCancel={onClose}
      onOk={isLastStep ? submit : goNext}
      confirmLoading={loading}
      okText={isLastStep ? (initial ? t('adversaireModal.enregistrer') : t('adversaireModal.ajouter')) : t('adversaireModal.continuer')}
      cancelText={t('adversaireModal.annuler')}
      /* LARGEUR : 1100 AU LIEU DE 800, ET BORNEE A LA FENETRE.

         A 800 pixels, une paire de champs laissait environ 370 pixels a chacun : une
         immatriculation, un numero de permis ou une raison sociale de compagnie y
         tenaient a peine, et il fallait faire defiler le champ pour relire ce qu'on
         venait de taper. Les lignes restent a deux ou trois champs - c'est la place de
         chacun qui change, non le decoupage.

         `min(..., 94vw)` et non 1100 sec : la modale ne se dimensionne pas toute seule,
         et 1100 deborderait d'un portable. Le reste de la largeur laisse voir le dossier
         derriere, puisque cette modale ne masque pas son fond. */
      width="min(1100px, 94vw)"
      okButtonProps={{
        style: { background: GREEN, borderColor: GREEN, minWidth: 130, height: 38, borderRadius: 9, fontWeight: 600 },
        disabled: isLastStep && !identifiable,
      }}
      cancelButtonProps={{ style: { height: 38, borderRadius: 9 } }}
      styles={{
        header: {
          background: GREEN,
          borderBottom: 'none',
          padding: '15px 22px',
          marginBottom: 0,
          borderRadius: '10px 10px 0 0',
        },
        content: {
          borderRadius: 12,
          overflow: 'hidden',
          padding: 0,
          border: '1px solid #cbd3e0',
          boxShadow: '0 28px 56px -16px rgba(16,24,40,0.34), 0 8px 20px -12px rgba(16,24,40,0.22)',
        },
        body: { padding: '20px 22px 4px' },
      }}
      closeIcon={<span style={{ color: '#fff', fontSize: 17 }}>×</span>}
      destroyOnClose
    >
      <Form form={form} layout="vertical" requiredMark={false}>

        <Steps
          current={currentStep}
          size="small"
          items={steps.map(s => ({ title: s.title }))}
          style={{ marginBottom: 22 }}
        />

        {/* ══════ Étape 1 · Nature de la partie adverse ══════ */}
        <div style={{ display: stepKey === 'nature' ? 'block' : 'none' }}>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Form.Item label={lbl(t('adversaireModal.champs.typePartieAdverse'), true)} style={{ marginBottom: 20 }}>
                <Select
                  {...inputH}
                  value={natureKey}
                  onChange={choisirNature}
                  optionLabelProp="selectedLabel"
                  options={optionsNature()}
                  listHeight={300}
                />
              </Form.Item>
            </Col>
          </Row>

          {typeAdversaire === 'AUTRE' && (
            <Row gutter={16}>
              <Col xs={24} sm={16}>
                <Form.Item
                  name="typeAdversaireAutre"
                  label={lbl(t('adversaireModal.champs.naturePrecise'), true)}
                  rules={[{ required: true, message: t('adversaireModal.precisezHeurte') }]}
                >
                  <Input {...inputH} placeholder={t('adversaireModal.exemples.bienHeurte')} maxLength={200} />
                </Form.Item>
              </Col>
            </Row>
          )}

        </div>

        {/* ══════ Étape 2 · Conducteur adverse ou piéton ══════
            Monté seulement hors « bien » : ce bloc et le bloc « bien » partagent
            les noms `nom` / `telephone` / `descriptionDegats`, et deux Form.Item
            de même nom cumuleraient leurs règles. */}
        {!estBien && (
          <div style={{ display: stepKey === 'partie' ? 'block' : 'none' }}>
            <div style={sectionLabel}>{estEngin ? t('adversaireModal.sections.conducteurAdverse') : t('adversaireModal.sections.pietonHeurte')}</div>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="nom"
                  label={lbl(t('adversaireModal.champs.nom'), true)}
                  rules={[regleIdentite]}
                >
                  <Input {...inputH} placeholder="LAKMON" maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="prenoms" label={lbl(t('adversaireModal.champs.prenoms'))}>
                  <Input {...inputH} placeholder="Adjikamba" maxLength={200} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="dateNaissance" label={lbl(t('adversaireModal.champs.dateNaissance'))}>
                  <DatePicker
                    {...dateH}
                    format="DD/MM/YYYY"
                    placeholder={t('adversaireModal.formatDate')}
                    disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="telephone" label={lbl(t('adversaireModal.champs.telephone'))}>
                  <Input {...inputH} placeholder="+228 90 00 00 00" maxLength={30} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="email" label={lbl(t('adversaireModal.champs.courriel'))} rules={[{ type: 'email', message: t('adversaireModal.adresseInvalide') }]}>
                  <Input {...inputH} placeholder={t('adversaireModal.exemples.courriel')} maxLength={150} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="nationaliteChoix" label={lbl(t('adversaireModal.champs.nationalite'))}>
                  <Select
                    {...inputH}
                    showSearch
                    allowClear
                    placeholder={t('adversaireModal.paysNationalite')}
                    optionFilterProp="label"
                    options={[
                      ...paysList.map(p => ({ label: p.libelle, value: p.libelle })),
                      { label: t('adversaireModal.horsCedeaoAPreciser'), value: HORS_CEDEAO },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {nationaliteChoix === HORS_CEDEAO && (
              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <Form.Item
                    name="nationaliteAutre"
                    label={lbl(t('adversaireModal.champs.paysHorsCedeao'), true)}
                    rules={[{ required: true, message: t('adversaireModal.precisezPaysHorsCedeao') }]}
                  >
                    <Input {...inputH} placeholder={t('adversaireModal.exemples.paysHorsCedeao')} maxLength={50} />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {estPieton && blocPrejudiceCorporel}

            {estPieton && blocResponsabilite}

            {estPieton && (
              <Form.Item name="descriptionDegats" label={lbl(t('adversaireModal.champs.description'))}>
                <TextArea
                  rows={3}
                  placeholder={t('adversaireModal.exemples.circonstances')}
                  maxLength={1000}
                  showCount
                  style={{ borderRadius: 8, borderColor: LINE }}
                />
              </Form.Item>
            )}

            {estEngin && (
              <>
                <div style={{ ...sectionLabel, marginTop: 8 }}>{t('adversaireModal.sections.permis')}</div>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="numeroPermis" label={lbl(t('adversaireModal.champs.numeroPermis'))}>
                      <Input {...inputH} placeholder={t('adversaireModal.exemples.numeroPermis')} maxLength={30} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="categoriesPermis" label={lbl(t('adversaireModal.champs.categories'))}>
                      <Select
                        style={{ ...inputH.style, height: 'auto', minHeight: 38 }}
                        mode="multiple"
                        placeholder="A, B, C…"
                        maxTagCount="responsive"
                        optionFilterProp="label"
                        options={CATEGORIES_PERMIS.map(c => ({ label: c.label, value: c.value }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="dateDelivrance" label={lbl(t('adversaireModal.champs.dateDelivrance'))}>
                      <DatePicker
                        {...dateH}
                        format="DD/MM/YYYY"
                        placeholder={t('adversaireModal.formatDate')}
                        disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="lieuDelivrance" label={lbl(t('adversaireModal.champs.lieuDelivrance'))}>
                      <Input {...inputH} placeholder={t('adversaireModal.exemples.lieuDelivrance')} maxLength={100} />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>

        )}

        {/* ══════ Étape 3 · Engin adverse ══════ */}
        {estEngin && (
          <div style={{ display: stepKey === 'vehicule' ? 'block' : 'none' }}>
            <div style={sectionLabel}>{t('adversaireModal.sections.enginAdverse')}</div>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="immatriculation"
                  label={lbl(t('adversaireModal.champs.immatriculation'), true)}
                  rules={[{ required: true, message: t('adversaireModal.immatriculationRequise') }]}
                >
                  <Input
                    {...inputH}
                    placeholder="TG-1234-AB"
                    maxLength={30}
                    style={{ ...inputH.style, letterSpacing: '0.04em' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="marqueVehicule" label={lbl(t('adversaireModal.champs.marque'))}>
                  <SelectWithAdd
                    categorie="MARQUE"
                    valueKey="valeur"
                    placeholder={t('adversaireModal.selectionnerMarque')}
                    addLabel={t('adversaireModal.ajouterMarque')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="modeleVehicule" label={lbl(t('adversaireModal.champs.modele'))}>
                  <Input {...inputH} placeholder={t('adversaireModal.exemples.modele')} maxLength={100} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name="genreVehicule" label={lbl(t('adversaireModal.champs.genre'))}>
                  <Select {...inputH} allowClear placeholder={t('adversaireModal.champs.genre')} options={GENRES_VEHICULE} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={6}>
                <Form.Item name="couleurVehicule" label={lbl(t('adversaireModal.champs.couleur'))}>
                  <Input {...inputH} placeholder={t('adversaireModal.exemples.couleur')} maxLength={50} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <Form.Item name="prochaineVT" label={lbl(t('adversaireModal.champs.prochaineVt'))}>
                  <DatePicker {...dateH} format="DD/MM/YYYY" placeholder={t('adversaireModal.formatDate')} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="capaciteVehicule" label={lbl(t('adversaireModal.champs.places'))}>
                  <CBInputNumber style={{ ...inputH.style, width: '100%' }} min={1} max={150} placeholder="1" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="nbPersonnesABord" label={lbl(t('adversaireModal.champs.personnesABord'))}>
                  <CBInputNumber style={{ ...inputH.style, width: '100%' }} min={0} max={150} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="proprietaireVehicule" label={lbl(t('adversaireModal.champs.proprietaireEngin'))}>
                  <Input {...inputH} placeholder={t('adversaireModal.siDifferentConducteur')} maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="numeroChassis" label={lbl(t('adversaireModal.champs.numeroChassis'))}>
                  <Input {...inputH} placeholder="VIN" maxLength={50} />
                </Form.Item>
              </Col>
            </Row>

            {/* Attelage : la remorque est un engin à part entière (plaque, marque,
                modèle et contrat d'assurance distincts du tracteur). */}
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: 12, padding: '12px 14px', borderRadius: 10,
                background: montrerRemorque ? GREEN_SOFT : PAPER,
                border: `1px solid ${montrerRemorque ? GREEN_LINE : LINE}`,
                marginBottom: montrerRemorque ? 18 : 8,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: montrerRemorque ? GREEN_DARK : INK }}>
                {t('adversaireModal.tracteRemorque')}
              </span>
              <Form.Item name="aRemorque" valuePropName="checked" noStyle>
                <Switch
                  checkedChildren={t('adversaireModal.oui')}
                  unCheckedChildren={t('adversaireModal.non')}
                  disabled={genreVehicule === 'REMORQUE'}
                  style={{ background: montrerRemorque ? GREEN : undefined }}
                />
              </Form.Item>
            </div>

            {montrerRemorque && (
              <>
                <div style={sectionLabel}>{t('adversaireModal.sections.remorque')}</div>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="remorqueImmatriculation"
                      label={lbl(t('adversaireModal.champs.remorqueImmatriculation'), true)}
                      rules={[{ required: true, message: t('adversaireModal.remorqueImmatriculationRequise') }]}
                    >
                      <Input
                        {...inputH}
                        placeholder="TG-5678-CD"
                        maxLength={50}
                        style={{ ...inputH.style, letterSpacing: '0.04em' }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="remorqueMarque" label={lbl(t('adversaireModal.champs.remorqueMarque'))}>
                      <SelectWithAdd
                        categorie="MARQUE"
                        valueKey="valeur"
                        placeholder={t('adversaireModal.selectionnerMarque')}
                        addLabel={t('adversaireModal.ajouterMarque')}
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="remorqueModele" label={lbl(t('adversaireModal.champs.remorqueModele'))}>
                      <Input {...inputH} placeholder={t('adversaireModal.exemples.remorqueModele')} maxLength={100} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="remorqueAssureur" label={lbl(t('adversaireModal.champs.remorqueAssureur'))}>
                      <Select
                        {...inputH}
                        showSearch
                        allowClear
                        placeholder={t('adversaireModal.champs.compagnieAssurance')}
                        filterOption={filtreCompagnie}
                        optionLabelProp="nom"
                        options={optionsCompagnies}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </div>
        )}

        {/* ══════ Étape 4 · Assurance & dommages - engin adverse uniquement ══════ */}
        {estEngin && (
          <div style={{ display: stepKey === 'dommages' ? 'block' : 'none' }}>
            <div style={sectionLabel}>{t('adversaireModal.sections.assurance')}</div>
            {/* LE CHAMP « TYPE DE DOMMAGE » A DISPARU : la situation le determine. La
                compagnie prend donc la ligne entiere. */}
            <Form.Item name="assureurAdverse" label={lbl(t('adversaireModal.champs.compagnieAssurance'))}>
              <Select
                {...inputH}
                showSearch
                allowClear
                placeholder={t('adversaireModal.compagnieEnginAdverse')}
                filterOption={filtreCompagnie}
                optionLabelProp="nom"
                options={optionsCompagnies}
              />
            </Form.Item>

            {montrerSituation && blocPrejudiceCorporel}

            {/* Un blessé est un blessé : la gravité ne se déclare pas, elle
                s'expertise. Un seul compteur de blessés, un de décès. */}
            <div style={sectionLabel}>{t('adversaireModal.sections.nombreAdversaires')}</div>

            <Row gutter={16}>
              <Col xs={12} sm={6}>
                <Form.Item name="blesses" label={lbl(t('adversaireModal.champs.blesses'))}>
                  <CBInputNumber style={{ ...inputH.style, width: '100%' }} min={0} max={50} placeholder="0" />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item name="deces" label={lbl(t('adversaireModal.champs.deces'))}>
                  <CBInputNumber style={{ ...inputH.style, width: '100%' }} min={0} max={50} placeholder="0" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="descriptionDegats" label={lbl(degatsLabel)}>
              <TextArea
                rows={3}
                placeholder={t('adversaireModal.exemples.degatsEngin')}
                maxLength={1000}
                showCount
                style={{ borderRadius: 8, borderColor: LINE }}
              />
            </Form.Item>

            {blocResponsabilite}
          </div>
        )}

        {/* ══════ Étape 2 bis · Bien heurté - un seul écran ══════ */}
        {estBien && (
          <div style={{ display: stepKey === 'bien' ? 'block' : 'none' }}>
            <div style={sectionLabel}>{t('adversaireModal.sections.proprietaireBien')}</div>

            <Row gutter={16}>
              <Col xs={24} sm={14}>
                <Form.Item name="nom" label={lbl(t('adversaireModal.champs.nomProprietaire'))}>
                  <Input {...inputH} placeholder={t('adversaireModal.exemples.nomProprietaire')} maxLength={200} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={10}>
                <Form.Item name="telephone" label={lbl(t('adversaireModal.champs.telephone'))}>
                  <Input {...inputH} placeholder="+228 90 00 00 00" maxLength={30} />
                </Form.Item>
              </Col>
            </Row>

            <div style={{ ...sectionLabel, marginTop: 8 }}>{t('adversaireModal.sections.degatsConstates')}</div>

            <Form.Item
              name="descriptionDegats"
              label={lbl(t('adversaireModal.champs.description'), true)}
              rules={[{ required: true, message: t('adversaireModal.decrivezDegats') }]}
            >
              <TextArea
                rows={4}
                placeholder={t('adversaireModal.exemples.degatsBien')}
                maxLength={1000}
                showCount
                style={{ borderRadius: 8, borderColor: LINE }}
              />
            </Form.Item>
          </div>

        )}

        {/* ══════ Négociation RC - uniquement pour un adversaire déjà créé ══════ */}
        {initial?.victimeTrackingId && (
          <div style={{ display: stepKey === 'rc' ? 'block' : 'none' }}>
            <div style={sectionLabel}>{t('adversaireModal.positionRc')}</div>
            <NegociationRcPanel adversaire={initial} onUpdated={onRcUpdated} />
          </div>
        )}

        <div
          style={{
            marginTop: 12,
            paddingTop: 14,
            borderTop: `1px solid ${LINE}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <RequiredLegend />
          {currentStep > 0 && (
            <Button onClick={goPrev} style={{ height: 34, borderRadius: 9 }}>{t('adversaireModal.precedent')}</Button>
          )}
        </div>
      </Form>
    </CBModal>
  );
}
